import React, { useState } from 'react';
import './TrimControl.css';

/**
 * Trim a track in the lesson (2026-09-28). Anthony: "Is there a way to add a
 * trim option for tracks?", then "Both": start / end points now, with no new
 * file, and a rendered "save as a cut" later.
 *
 * The trim is stored on the student's song for the track's TIMELINE (the same
 * key the section marks use, so a job's Reference, Track and Vocals share it)
 * and the page hands it to the player, which starts at the start and stops at
 * the end. The original file is untouched; Clear puts the whole track back.
 * Lesson Dashboard only: the family Songs page still plays the whole file.
 *
 * One quiet line when not editing ("Whole track" / "Plays 0:12 to 2:40"),
 * with Edit always visible; once trimmed, **Save as a cut** renders the span
 * as a real file (a lesson track's Reference / Track / Vocals together). Editing: play to a spot and tap Start here or
 * End here.
 */
const fmt = (t) => {
	const s = Math.max(0, Math.floor(Number(t) || 0));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const TrimControl = ({ file, player, trim = null, onSave = null, onSaveCut = null, cutWhat = '' }) => {
	const [editing, setEditing] = useState(false);
	const [error, setError] = useState('');
	const [confirming, setConfirming] = useState(false);
	const [sent, setSent] = useState(false);
	if (!file || !player || !onSave) return null;
	const now = Number(player.currentTime) || 0;
	const duration = Number(player.duration) || 0;
	const start = trim ? trim.start || 0 : 0;
	const end = trim && trim.end !== null && trim.end !== undefined ? trim.end : null;

	const save = async (next) => {
		setError('');
		try {
			await onSave(file, next);
		} catch (e) {
			setError(`The trim did not save. Please tell Anthony right away. (${e && e.message ? e.message : 'unknown error'})`);
		}
	};
	const setStart = () => {
		if (end !== null && now >= end) return setError('The start has to come before the end.');
		return save({ start: now, end });
	};
	const setEnd = () => {
		if (now <= start) return setError('The end has to come after the start.');
		return save({ start, end: duration && now >= duration - 0.2 ? null : now });
	};

	const summary = trim ? `Plays ${fmt(start)} to ${end === null ? 'the end' : fmt(end)}` : 'Whole track';

	// "Save as a cut" (the second half, 2026-09-28): render the trimmed span as
	// a real, shorter file the family can play and download. Two taps: the
	// first says what will be made, the second queues it.
	const cutEnd = end === null ? duration : end;
	const cutLen = cutEnd - start;
	const canCut = Boolean(onSaveCut && trim && cutLen >= 5);
	const saveCut = async () => {
		setError('');
		try {
			await onSaveCut(file, { start, end: cutEnd });
			setConfirming(false);
			setSent(true);
		} catch (e) {
			setError(`The cut could not be queued. Please tell Anthony right away. (${e && e.message ? e.message : 'unknown error'})`);
		}
	};

	return (
		<div className={`ld-trim${editing ? ' is-editing' : ''}`} role="group" aria-label="Trim">
			<span className="ld-trim__label">
				<i className="fa-sharp fa-solid fa-scissors" aria-hidden="true" />
				Trim
			</span>
			<span className={`ld-trim__summary${trim ? ' is-set' : ''}`}>{summary}</span>
			{editing ? (
				<>
					<button type="button" className="ld-trim__btn" onClick={setStart}>
						Start here ({fmt(now)})
					</button>
					<button type="button" className="ld-trim__btn" onClick={setEnd}>
						End here ({fmt(now)})
					</button>
					{trim && (
						<button type="button" className="ld-trim__btn" onClick={() => save(null)}>
							Clear
						</button>
					)}
					<button type="button" className="ld-trim__btn ld-trim__btn--done" onClick={() => setEditing(false)}>
						Done
					</button>
					<p className="ld-trim__note">Play to a spot, then tap Start here or End here. The file itself is not changed.</p>
				</>
			) : (
				<>
					<button type="button" className="ld-trim__btn" onClick={() => setEditing(true)}>
						Edit
					</button>
					{canCut && !confirming && (
						<button type="button" className="ld-trim__btn ld-trim__btn--done" onClick={() => { setSent(false); setConfirming(true); }}>
							Save as a cut
						</button>
					)}
				</>
			)}
			{confirming && !editing && (
				<div className="ld-trim__confirm">
					<p className="ld-trim__note">
						Makes a new {fmt(cutLen)} file{cutWhat ? ` of the ${cutWhat}` : ''} from {fmt(start)} to {fmt(cutEnd)}, on a studio Mac. The family can play and download it; the original stays.
					</p>
					<button type="button" className="ld-trim__btn ld-trim__btn--done" onClick={saveCut}>
						Make the cut
					</button>
					<button type="button" className="ld-trim__btn" onClick={() => setConfirming(false)}>
						Cancel
					</button>
				</div>
			)}
			{sent && !confirming && <p className="ld-trim__note">Cut queued. It appears with this song's tracks when a studio Mac finishes it.</p>}
			{error && (
				<div className="ld-dismissable-error" role="alert">
					<p>{error}</p>
					<button type="button" className="ld-dismiss" onClick={() => setError('')}>
						Dismiss
					</button>
				</div>
			)}
		</div>
	);
};

export default TrimControl;
